/**
 * src/stores/projectMigrations.ts
 *
 * Pure leaf module (SAFE-05). Contains `migrateAdjustmentField` (moved verbatim from
 * src/stores/migrate.ts) and the new `migrateProjectStore` persist `migrate` callback for
 * `useProjectStore`.
 *
 * Deliberately imports NOTHING from any store. `src/stores/migrate.ts` imports `useSyncStore`
 * (for `migrateLocalToCloud`'s conflict-badge signal), and `src/stores/useSyncStore.ts` imports
 * `useProjectStore` — so wiring `useProjectStore`'s `migrate` callback from `migrate.ts` would
 * create a `useProjectStore -> migrate.ts -> useSyncStore.ts -> useProjectStore.ts` import
 * cycle sitting on the boot path of the store that owns the user's entire project library. This
 * module is a leaf so `useProjectStore.ts` can import from it with zero cycle risk.
 */

/**
 * Migrates a raw persisted EstimateItem's `adjustment` field from the legacy
 * string|number form to the typed { op: 'add'|'sub'|'mul', value: number } form.
 *
 * All 9 legacy forms:
 *   '+5'  → { op:'add', value:5 }       (starts with '+' followed by digits)
 *   '-3.5'→ { op:'sub', value:3.5 }     (starts with '-' followed by digits)
 *   '2'   → { op:'mul', value:2 }       (bare number string = multiplier)
 *   '1'   → { op:'mul', value:1 }       (identity multiplier)
 *   1     → { op:'mul', value:1 }       (number form = multiplier)
 *   '0'   → { op:'mul', value:0 }       (zero multiplier)
 *   '++5' → { op:'mul', value:1 }       (malformed → safe identity, no data loss)
 *   '5+2' → { op:'mul', value:1 }       (malformed → safe identity, no data loss)
 *   '-'   → { op:'mul', value:1 }       (malformed → safe identity, no data loss)
 *
 * Note on intentional behavior change: '++5' previously silently computed as +5
 * in the regex calculator (strip all operators → parse '5' → adj startsWith('+') → +=5).
 * The typed migration maps it to identity { op:'mul', value:1 } which is safer
 * (no silent truncation). This is documented in 05-01-SUMMARY.md.
 *
 * Already-typed objects (adjustment is an object) are returned unchanged (same reference).
 * null/undefined adjustment fields are left unchanged.
 *
 * Never throws — any parse error yields the safe identity default.
 */
export function migrateAdjustmentField(rawItem: Record<string, unknown>): Record<string, unknown> {
  const adj = rawItem.adjustment;

  // Already typed object (or null/undefined) → unchanged
  if (adj === null || adj === undefined || typeof adj === 'object') {
    return rawItem;
  }

  const adjStr = String(adj).trim();
  let typed: { op: 'add' | 'sub' | 'mul'; value: number };

  if (/^\+[\d.]+$/.test(adjStr)) {
    // '+5', '+3.5', '+0'
    typed = { op: 'add', value: parseFloat(adjStr.slice(1)) || 0 };
  } else if (/^-[\d.]+$/.test(adjStr)) {
    // '-3.5', '-0'
    typed = { op: 'sub', value: parseFloat(adjStr.slice(1)) || 0 };
  } else if (/^[\d.]+$/.test(adjStr)) {
    // '2', '1', '0', '1.1'
    typed = { op: 'mul', value: parseFloat(adjStr) };
  } else {
    // Malformed: '++5', '5+2', '-', '' → safe identity no-op
    typed = { op: 'mul', value: 1 };
  }

  return { ...rawItem, adjustment: typed };
}

/**
 * Persist `migrate` callback for `useProjectStore` (SAFE-05). Bumps version 1 → 2: backfills
 * `nameIsManual: false` on room items and project-level (global layer) items that predate the
 * field, and converts legacy string/number `adjustment` values to the typed form via
 * `migrateAdjustmentField`. Default FIRST, item spread LAST in `{ nameIsManual: false, ...item }`
 * so an existing `nameIsManual: true` is preserved (T-04-01 mitigation) — reversing that order
 * would clobber user data.
 *
 * Operates on the INNER persisted state object (`persistedState`), NOT the outer
 * `{ state, version }` envelope that the deleted raw-blob schema-version migration function used
 * to read — persist's `migrate` option always receives the inner state object directly (confirmed
 * against `migrateUIStore` in src/stores/useUIStore.ts, which receives `{ theme, currency }`,
 * never `{ state, version }`).
 *
 * The entire body is wrapped in try/catch as a DATA-SAFETY REQUIREMENT, not hardening: if this
 * callback throws, zustand's internal `.catch()` (verified against the installed
 * zustand@5.0.13 source) swallows the error and never calls `set()`, leaving the store at
 * `create()`'s bare defaults while persist's write-through patch stays armed on every
 * subsequent `set()`. The user's very next action would then silently overwrite their real
 * localStorage blob with empty data — permanent loss, strictly worse than the
 * stale-until-reload bug this migration fixes. The catch returns `persistedState` completely
 * unchanged (the one restore point): with a malformed blob, staying stale (recoverable by a
 * future fix) beats losing the data outright.
 *
 * No branch handles `persistedState === undefined`: persist's `migrate` option is only ever
 * invoked when a persisted blob already exists AND its version differs from the current one
 * (verified against the installed zustand@5.0.13 source — `if (deserializedStorageValue) { if
 * (version !== options.version) { migrate(...) } }`). A first-ever visit takes the
 * `return [false, void 0]` branch and never reaches this function, so such a branch would be
 * unreachable dead code.
 */
export function migrateProjectStore(persistedState: unknown, version: number): unknown {
  try {
    let state = persistedState as {
      rooms?: unknown;
      projectItems?: unknown;
    };

    if (version < 2) {
      const rawRooms = state?.rooms;
      const migratedRooms = Array.isArray(rawRooms)
        ? (rawRooms as Array<Record<string, unknown> & { items?: unknown }>).map((room) => {
            const items = room.items;
            if (!Array.isArray(items) || items.length === 0) return room;
            return {
              ...room,
              items: (items as Array<Record<string, unknown>>).map((item) =>
                migrateAdjustmentField({ nameIsManual: false, ...item })
              ),
            };
          })
        : rawRooms;

      const rawProjectItems = state?.projectItems;
      const migratedProjectItems = Array.isArray(rawProjectItems)
        ? (rawProjectItems as Array<Record<string, unknown>>).map((item) =>
            migrateAdjustmentField({ nameIsManual: false, ...item })
          )
        : rawProjectItems;

      state = {
        ...state,
        ...(migratedRooms !== undefined && { rooms: migratedRooms }),
        ...(migratedProjectItems !== undefined && { projectItems: migratedProjectItems }),
      };
    }

    return state;
  } catch (e) {
    console.warn('[migrateProjectStore] Migration failed, returning state unchanged:', e);
    return persistedState;
  }
}
